/**
 * `when` is filtered ONCE, here, for both hosts (#1466).
 *
 * `core.layout_rule_verdicts` answers show / hide / unresolved for the rule keys
 * a LayoutDocument references, with the record in context and read as the
 * caller. This turns those verdicts into a filtered document — and it is
 * deliberately the only place that does, because two hosts each writing their
 * own `verdict === 'show'` is two chances to get `unresolved` wrong.
 *
 * ── the rule that is easy to get wrong ──────────────────────────────────────
 *
 * `unresolved` HIDES, exactly like `hide`. It is a different word because a
 * deleted rule and a deliberate `false` are different facts, and collapsing
 * them produces the failure this exists to prevent: an author believing a field
 * is conditional while it is simply always gone. So the hiding is the same and
 * the REPORTING is not — every unresolved key comes back as a named issue for
 * the host to surface.
 *
 * ── fail closed, and say so ─────────────────────────────────────────────────
 *
 * A key with no verdict at all — the door was never called, the call failed,
 * the response was truncated — is treated as `unresolved`. The alternative is
 * showing a field whose condition nobody evaluated, which on a record page is a
 * disclosure rather than a display bug. Absence of an answer is not permission.
 */
import type { PersistedLayoutDocument } from './layout.js'

/* The persisted document's section type is a UNION -- a v1 scalar column count
   or a v2 responsive track -- so this derives the element type rather than
   naming `LayoutSection`, which is only one arm of it. Spreading into the
   named type widened the union and tsc caught it; vitest did not, because a
   test run does not typecheck. */
type PersistedSection = PersistedLayoutDocument['sections'][number]
type PersistedItem = PersistedSection['items'][number]

export type WhenVerdict = 'show' | 'hide' | 'unresolved'

/** What `core.layout_rule_verdicts` returns, one row per requested key. */
export interface RuleVerdict { rule: string, verdict: WhenVerdict }

export interface WhenIssue {
  /** The rule key that could not be settled. */
  rule: string
  /** The item it gated, so a host can say WHICH control vanished. */
  itemKey: string
  reason: 'unresolved'
}

export interface FilteredLayout<D extends PersistedLayoutDocument = PersistedLayoutDocument> {
  document: D
  /** Empty when every referenced rule was settled. Never null. */
  issues: WhenIssue[]
}

/** Every rule key a document references, de-duplicated, for one door call. */
export function referencedRules(document: PersistedLayoutDocument): string[] {
  const keys = new Set<string>()
  for (const section of document.sections) {
    for (const item of section.items) {
      if (item.when?.rule) keys.add(item.when.rule)
    }
  }
  return [...keys].sort()
}

/**
 * Apply server verdicts to a document.
 *
 * Items WITHOUT a `when` are untouched — the absence of a condition is not an
 * unresolved condition, and treating it as one would empty every form that
 * never used the feature.
 */
export function applyWhen<D extends PersistedLayoutDocument>(
  document: D,
  verdicts: readonly RuleVerdict[],
): FilteredLayout<D> {
  const byRule = new Map(verdicts.map((v) => [v.rule, v.verdict]))
  const issues: WhenIssue[] = []

  const keep = (item: PersistedItem): boolean => {
    const rule = item.when?.rule
    if (!rule) return true
    const verdict = byRule.get(rule) ?? 'unresolved'
    if (verdict === 'show') return true
    if (verdict === 'unresolved') {
      issues.push({ rule, itemKey: item.key, reason: 'unresolved' })
    }
    return false
  }

  /* GENERIC so each section keeps its own arm of the union. Mapping the union
     directly and spreading produced a widened object whose `items` satisfied
     neither arm, which is what tsc reported. Filtering never changes an item's
     type, so returning `S` is sound rather than convenient. */
  const filtered = <S extends PersistedSection>(section: S): S =>
    ({ ...section, items: section.items.filter(keep) }) as S

  /* The DOCUMENT is a union too — a v1 document holds v1 sections and a v2 one
     holds v2 sections, never a mixture — so mapping produced `(A | B)[]` where
     the type wants `A[] | B[]`. Generic over the document with one cast at the
     boundary keeps that homogeneity, which filtering cannot break. */
  return {
    document: {
      ...document,
      sections: (document.sections as PersistedSection[]).map((s) => filtered(s)),
    } as D,
    issues,
  }
}

/**
 * One sentence a host can render for the issues, or null when there are none.
 *
 * Deliberately names how many controls are affected and not which rule failed:
 * a rule key is an internal identifier, and a person reading a record page can
 * do nothing with `crm/lead_is_qualified`. The keys stay in `issues` for an
 * operator reading logs.
 */
export function whenIssueMessage(issues: readonly WhenIssue[]): string | null {
  if (issues.length === 0) return null
  const n = issues.length
  return `${n} ${n === 1 ? 'field is' : 'fields are'} hidden because this deployment `
    + 'could not check the condition that controls them. Ask an administrator to review the form.'
}
